import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { arrowBack, notificationsOutline, settingsOutline } from 'ionicons/icons';
import BrandMark from '../BrandMark';
import campoPhoto from '../../../assets/campo.jpg';
import { useAuth } from '../../../application/use-cases/auth/AuthContext';
import type { SkyMood } from '../../../domain/SkyMood';
import WeatherFx from './WeatherFx';
import './home.css';

// Lo que el Inicio le pasa al home del lote o de la estación para su encabezado.
export interface HeroExtras {
  switcher?: React.ReactNode;
  alertCount: number;
  // Abierto desde otra pantalla (una estación de un lote): flecha para volver.
  onBack?: () => void;
}

interface Props {
  mood: SkyMood;
  title: string;
  status?: { label: string; online: boolean };
  meta: { icon: string; text: string }[];
  // Selector de lote/estación (si el usuario tiene más de uno).
  switcher?: React.ReactNode;
  alertCount: number;
  onBack?: () => void;
  // Configurar lo que se está viendo (la estación).
  onSettings?: () => void;
  // Lo que va "colgado" del borde inferior (la condición del lote).
  children?: React.ReactNode;
}

// La foto del campo de fondo. De noche se oscurece y nublado se apaga (ver
// home.css); los efectos de clima van encima.
const HERO_PHOTO = campoPhoto;

// "Domingo 11 de octubre · 23:34", al cambiar el minuto se actualiza.
const HeroClock: React.FC = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setNow(new Date());
      interval = window.setInterval(() => setNow(new Date()), 60_000);
    }, 60_000 - (Date.now() % 60_000));
    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(interval);
    };
  }, []);
  const day = now.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
  const time = now.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
  return (
    <time className="home-hero-clock" dateTime={now.toISOString()}>
      {day.charAt(0).toUpperCase() + day.slice(1).replace(',', '')} · {time}
    </time>
  );
};

// Encabezado del Inicio: una foto del campo tratada según la hora y el
// clima, con efectos sutiles (viento, sol, lluvia), y encima el nombre del
// lote o la estación. Las alertas y la cuenta quedan a mano arriba a la derecha.
const HomeHero: React.FC<Props> = ({ mood, title, status, meta, switcher, alertCount, onBack, onSettings, children }) => {
  const history = useHistory();
  const { user } = useAuth();
  const initial = (user?.name ?? '?').trim().charAt(0).toUpperCase();
  // Para probar otra foto: ?foto=<id de Unsplash> (solo vista previa).
  const asked = new URLSearchParams(window.location.search).get('foto');
  const photo =
    asked && /^photo-[\w-]+$/.test(asked) ? `https://images.unsplash.com/${asked}?w=1400&q=80&auto=format&fit=crop` : HERO_PHOTO;

  return (
    <div className="home-hero-wrap">
      <header className={`home-hero phase-${mood.phase} wind-${mood.wind}${children ? ' has-overlap' : ''}`}>
        <div className={`home-hero-photo clouds-${mood.clouds}`} style={{ backgroundImage: `url(${photo})` }} />
        <WeatherFx mood={mood} />
        <div className="home-hero-shade" />
        <div className="home-hero-content">
          <div className="home-hero-top">
            {onBack ? (
              <button className="home-hero-icon home-hero-back" aria-label="Volver" onClick={onBack}>
                <IonIcon icon={arrowBack} />
              </button>
            ) : (
              <span className="home-brand">
                <BrandMark className="home-brand-mark" />
                DomIoT
              </span>
            )}
            {onSettings && (
              <button className="home-hero-icon" aria-label="Configurar estación" onClick={onSettings}>
                <IonIcon icon={settingsOutline} />
              </button>
            )}
            <button className="home-hero-icon" aria-label="Alertas" onClick={() => history.push('/app/alerts')}>
              <IonIcon icon={notificationsOutline} />
              {alertCount > 0 && <span className="home-hero-badge">{alertCount}</span>}
            </button>
            <button className="home-hero-avatar" aria-label="Cuenta" onClick={() => history.push('/app/settings')}>
              {initial}
            </button>
          </div>

          {switcher}

          <div className="home-hero-title">
            <h1>{title}</h1>
            {status && <span className={`home-hero-status ${status.online ? 'is-online' : 'is-offline'}`}>{status.label}</span>}
          </div>
          {meta.length > 0 && (
            <div className="home-hero-meta">
              {meta.map((m) => (
                <span key={m.text}>
                  <IonIcon icon={m.icon} />
                  {m.text}
                </span>
              ))}
            </div>
          )}
          <HeroClock />
        </div>
      </header>
      {children}
    </div>
  );
};

export default HomeHero;
